
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { ModuleCard } from "./ModuleCard";

interface ModulesProps {
  topicId: string;
  onBack: () => void;
  onSelectModule: (moduleId: string) => void;
}

/**
 * Modules component - displays all modules for a topic with progress tracking
 * Calculates and shows completion progress for each module based on lessons completed
 */
export const Modules = ({ topicId, onBack, onSelectModule }: ModulesProps) => {
  // Fetch topic details
  const { data: topic } = useQuery({
    queryKey: ['topic', topicId],
    queryFn: async () => {
      console.log("🔍 Fetching topic details for:", topicId);
      const { data, error } = await supabase
        .from('topics')
        .select('*')
        .eq('id', topicId)
        .maybeSingle();
      
      if (error) {
        console.error("❌ Error fetching topic:", error);
        throw error;
      }

      return data;
    },
  });

  // Fetch modules for this topic
  const { data: modules = [], isLoading: isLoadingModules } = useQuery({
    queryKey: ['modules', topicId],
    queryFn: async () => {
      console.log("📚 Fetching modules for topic:", topicId);
      const { data, error } = await supabase
        .from('modules')
        .select('*')
        .eq('topic_id', topicId)
        .eq('status', 'active')
        .order('order_index');
      
      if (error) {
        console.error("❌ Error fetching modules:", error);
        throw error;
      }

      console.log("✅ Modules fetched:", data?.length || 0);
      return data;
    },
  });

  // Fetch user progress for all lessons
  const { data: userProgress = [], isLoading: isLoadingProgress } = useQuery({
    queryKey: ['userProgressModules'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        console.log("👤 No user session found for progress tracking");
        return [];
      }

      console.log("📈 Fetching user progress for modules");
      const { data, error } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', session.user.id);

      if (error) {
        console.error("❌ Error fetching user progress:", error);
        throw error;
      }

      console.log("📊 User progress data:", data?.length || 0, "records");
      return data;
    },
  });

  // Fetch lessons for all modules to calculate progress
  const { data: allLessons = [], isLoading: isLoadingLessons } = useQuery({
    queryKey: ['lessonsForModules', modules.map(m => m.id)],
    queryFn: async () => {
      if (modules.length === 0) return [];

      console.log("📝 Fetching lessons for modules");
      const { data, error } = await supabase
        .from('lessons')
        .select('id, module_id')
        .in('module_id', modules.map(m => m.id))
        .eq('status', 'active');

      if (error) {
        console.error("❌ Error fetching lessons:", error);
        throw error;
      }

      console.log("📝 Lessons fetched:", data?.length || 0);
      return data;
    },
    enabled: modules.length > 0,
  });

  // Calculate progress for each module
  const getModuleProgress = (moduleId: string) => {
    const moduleLessons = allLessons.filter(lesson => lesson.module_id === moduleId);
    const totalLessons = moduleLessons.length;

    if (totalLessons === 0) {
      return {
        totalLessons: 0,
        completedLessons: 0,
        progressPercentage: 0
      };
    }

    // Count completed lessons for this module
    const completedLessons = moduleLessons.filter(lesson =>
      userProgress.some(progress => 
        progress.lesson_id === lesson.id && progress.status === 'completed'
      )
    ).length;

    const progressPercentage = Math.round((completedLessons / totalLessons) * 100);

    console.log("📈 Module progress calculated:", {
      moduleId,
      totalLessons,
      completedLessons,
      progressPercentage
    });

    return {
      totalLessons,
      completedLessons,
      progressPercentage
    };
  };

  if (isLoadingModules || isLoadingProgress || isLoadingLessons) {
    return (
      <div className="space-y-8 animate-fade-in">
        <div className="flex items-center gap-4 px-8">
          <Button variant="ghost" onClick={onBack} disabled>
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Topics
          </Button>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 px-8">
          {[1, 2, 3].map((i) => (
            <div 
              key={i}
              className="h-48 bg-gray-100 rounded-lg animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Back Button and Topic Info */}
      <div className="flex items-center gap-4 px-8">
        <Button
          variant="ghost"
          onClick={onBack}
          className="group hover:bg-gray-100 -ml-2"
        >
          <ArrowLeft className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
          Back to Topics
        </Button>
        {topic && (
          <div>
            <h1 className="text-2xl font-bold">{topic.title}</h1>
            <p className="text-gray-600">{modules.length} modules available</p>
          </div>
        )}
      </div>

      {/* Modules Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 px-8">
        {modules.map((module) => (
          <ModuleCard
            key={module.id}
            module={module}
            progress={getModuleProgress(module.id)}
            onClick={() => {
              console.log("🎯 Selected module:", { id: module.id, title: module.title });
              onSelectModule(module.id);
            }}
          />
        ))}
      </div>
    </div>
  );
};
